import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';

import { MIGRATION_BROWSER_PARAMS } from '../hooks/use-migration-browser-params';
import { useMigrationConversions } from '../hooks/use-migration-conversions';
import { useMigrationUploadActivity } from '../hooks/use-migration-upload-activity';
import {
  useDeleteUnusedRawUploadMutation,
  useDiscoverOrgRawUploadsMutation,
  useListOrgRawUploadsQuery,
  useListResourceImportSessionsQuery,
} from '../store/hooks';

import { DemoDataSeedButton } from './demo-data-seed-button';
import { MigrationImportsList } from './migration-imports-list';
import { OrganizationDataImportV2Section } from './organization-data-import-v2-section';
import { StagedResourcesSection } from './staged-resources-section';

interface DataImportSectionProps {
  organizationId: string;
}

/**
 * The v2 Data Import pipeline surface, inline in the org chrome and
 * URL-addressable (`dsession` query param selects a batch):
 *
 *   1. upload raw source files to the S3 landing zone,
 *   2. Go starts the migration agent to convert and stage each source,
 *   3. review a batch's staged resources here and commit them.
 */
export function DataImportSection({ organizationId }: DataImportSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('dsession');

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  if (sessionId) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            // Leaving the session takes its browse state with it. A stale
            // `dtype` left behind would reopen a group in the NEXT session
            // the operator opens (GDN-3875).
            onClick={() =>
              patch({
                dsession: null,
                ...Object.fromEntries(MIGRATION_BROWSER_PARAMS.map(p => [p, null])),
              })
            }
          >
            Back to imports
          </Button>
        </Box>
        {/* Keyed by session (GDN-3883). Without this the section is a living
            component whose `sessionId` prop changes under it, so moving between
            two session URLs kept the previous session's summary, verdict lanes,
            conflicts, match feed and providers on screen until each refetch
            landed — and kept its decisions, resolutions, pending selections and
            page number for good, which no query-level fix reaches. */}
        <StagedResourcesSection
          key={sessionId}
          organizationId={organizationId}
          sessionId={sessionId}
          showCommit
        />
      </Box>
    );
  }

  return (
    <DataImportOverview
      key={organizationId}
      organizationId={organizationId}
      // Entering a session clears the browse state for the same reason leaving
      // one does: a `dtype` already in the query string would drop the operator
      // into a group of a session they have never opened.
      onOpenBatch={id =>
        patch({
          dsession: id,
          ...Object.fromEntries(MIGRATION_BROWSER_PARAMS.map(p => [p, null])),
        })
      }
    />
  );
}

interface DataImportOverviewProps {
  organizationId: string;
  onOpenBatch: (sessionId: string) => void;
}

function DataImportOverview({ organizationId, onOpenBatch }: DataImportOverviewProps) {
  const uploadsBusy = useMigrationUploadActivity(organizationId);
  const [pollingInterval, setPollingInterval] = useState(5000);
  const uploadsQuery = useListOrgRawUploadsQuery(
    { organizationId },
    { pollingInterval, skipPollingIfUnfocused: true },
  );
  const [discoverUploads, discovery] = useDiscoverOrgRawUploadsMutation();
  const [deleteUpload] = useDeleteUnusedRawUploadMutation();
  const discovered = useRef(false);
  useEffect(() => {
    if (!uploadsQuery.isSuccess || discovered.current) return;
    discovered.current = true;
    void discoverUploads({ organizationId });
  }, [organizationId, uploadsQuery.isSuccess, uploadsQuery.data, discoverUploads]);
  const conversion = useMigrationConversions(
    organizationId,
    uploadsQuery.data?.uploads?.map(upload => upload.upload_id) ?? [],
  );
  const sessionsQuery = useListResourceImportSessionsQuery({ organizationId });
  const awaitingStaging = conversion.jobs.some(job => {
    if (job.status === 'pending' || job.status === 'running') return true;
    if (!job.published) return false;
    const session = sessionsQuery.data?.items?.find(
      item =>
        item.import_session_id === job.import_session_id || item.raw_batch_id === job.raw_batch_id,
    );
    return (
      !session || !['ready_for_review', 'completed', 'failed', 'cancelled'].includes(session.status)
    );
  });
  const refetchSessions = sessionsQuery.refetch;
  useEffect(() => {
    if (!awaitingStaging) return;
    const timer = setInterval(() => void refetchSessions(), 5000);
    return () => clearInterval(timer);
  }, [awaitingStaging, refetchSessions]);
  const [addBatchId, setAddBatchId] = useState<string | null>(null);
  useEffect(() => {
    const counting = uploadsQuery.data?.uploads?.some(
      upload =>
        upload.inventory_status === 'pending' ||
        upload.inventory_status === 'refreshing' ||
        upload.source_state === 'deleting',
    );
    setPollingInterval(uploadsBusy || counting || discovery.isLoading ? 5000 : 30000);
  }, [uploadsBusy, uploadsQuery.data, discovery.isLoading]);

  const refresh = () => {
    discovered.current = true;
    void discoverUploads({ organizationId, refreshExisting: true });
    void uploadsQuery.refetch();
    void sessionsQuery.refetch();
    conversion.refresh();
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload source files, then press Go. The migration agent analyzes and converts them, checks
        the results, and stages them for your review. Nothing is committed until you review and
        commit it.
      </Typography>
      <DemoDataSeedButton organizationId={organizationId} onSeeded={refresh} />
      <OrganizationDataImportV2Section
        organizationId={organizationId}
        onUploaded={() => void uploadsQuery.refetch()}
      />
      {conversion.launchError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {conversion.launchError}
        </Alert>
      )}
      {(uploadsQuery.isError || sessionsQuery.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Some imports could not be loaded. Refresh to try again.
        </Alert>
      )}
      {discovery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not refresh source files. Your uploaded data is unchanged. Refresh to try again.
        </Alert>
      )}
      <MigrationImportsList
        uploads={uploadsQuery.data?.uploads ?? []}
        sessions={sessionsQuery.data?.items ?? []}
        isLoading={uploadsQuery.isLoading || sessionsQuery.isLoading}
        hasLoadError={uploadsQuery.isError || sessionsQuery.isError}
        onRefresh={refresh}
        onOpenBatch={onOpenBatch}
        onAddFiles={setAddBatchId}
        onDelete={async uploadId => {
          await deleteUpload({ organizationId, uploadId }).unwrap();
          void uploadsQuery.refetch();
          conversion.refresh();
        }}
        conversionJobs={conversion.jobs}
        conversionEnabled={conversion.enabled}
        conversionStatusKnown={conversion.known && !uploadsQuery.isError && !sessionsQuery.isError}
        onGo={(rawId, retryOf) => void conversion.go(rawId, retryOf)}
        launchingSourceId={conversion.launchingSourceId}
        uploadsBusy={uploadsBusy || Boolean(addBatchId)}
      />
      <Dialog
        open={Boolean(addBatchId)}
        onClose={() => {
          if (!uploadsBusy) setAddBatchId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add files to batch</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These files are added to the existing batch. Allowed only before the batch has been
            converted.
          </Typography>
          {addBatchId && (
            <OrganizationDataImportV2Section
              organizationId={organizationId}
              uploadId={addBatchId}
              embedded
              onUploaded={() => {
                void uploadsQuery.refetch();
                setAddBatchId(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
